<template>
  <div id="mainContent" class="min-h-0 flex flex-col h-full">
    <div class="flex-1 flex flex-col min-h-0">
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden flex flex-col h-full">
        <!-- Header -->
        <div class="bg-primary/10 px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <i class="pi pi-list text-primary text-lg"></i>
            <div>
              <h1 class="text-xl font-bold text-primary">Application Logs</h1>
              <p class="text-xs text-text-muted">View persisted notifications and API transactions</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button icon="pi pi-refresh" label="Refresh" class="rounded-full" :loading="isLoading" @click="onRefresh" />
            <Button icon="pi pi-download" label="Export CSV" class="rounded-full" severity="secondary" :loading="isLoading" @click="onExport" />
            <Button icon="pi pi-trash" label="Clear All" class="rounded-full" severity="danger" :loading="isLoading" @click="onClear" />
          </div>
        </div>

        <!-- Filters -->
        <div class="p-4 border-b border-border bg-background flex flex-col gap-3">
          <div class="grid grid-cols-12 gap-3 items-end">
            <div class="col-span-3">
              <label class="text-xs font-semibold text-text-light">Level</label>
              <Dropdown
                v-model="levelFilter"
                :options="levelOptions"
                option-label="label"
                option-value="value"
                placeholder="All levels"
                class="w-full"
              />
            </div>
            <div class="col-span-3">
              <label class="text-xs font-semibold text-text-light">Category</label>
              <Dropdown
                v-model="categoryFilter"
                :options="categoryOptions"
                option-label="label"
                option-value="value"
                placeholder="All categories"
                class="w-full"
              />
            </div>
            <div class="col-span-4">
              <label class="text-xs font-semibold text-text-light">Date Range</label>
              <Calendar
                v-model="dateRange"
                selection-mode="range"
                date-format="yy-mm-dd"
                show-icon
                class="w-full"
                placeholder="Select range"
              />
            </div>
            <div class="col-span-2">
              <label class="text-xs font-semibold text-text-light">Search</label>
              <div class="flex items-center gap-2">
                <span class="pi pi-search text-text-muted"></span>
                <InputText v-model="searchTerm" class="w-full" placeholder="Message contains..." @keyup.enter="applyFilters" />
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <Button label="Apply Filters" icon="pi pi-filter" class="rounded-full" :loading="isLoading" @click="applyFilters" />
            <Button label="Reset" icon="pi pi-times" class="rounded-full" severity="secondary" :loading="isLoading" @click="resetFilters" />
          </div>
        </div>

        <!-- Table -->
        <div class="flex-1 min-h-0">
          <DataTable
            :value="tableRows"
            data-key="id"
            scrollable
            scroll-height="flex"
            show-gridlines
            striped-rows
            class="p-datatable-sm h-full"
            :loading="isLoading"
            :paginator="true"
            :rows="25"
            :rows-per-page-options="[25, 50, 100]"
            paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            empty-message="No logs found for the selected filters"
          >
            <Column field="timestamp" header="Timestamp" sortable style="width: 14rem">
              <template #body="{ data }">
                <div class="flex flex-col text-xs">
                  <span class="font-semibold text-text">{{ formatDate(data.timestamp) }}</span>
                  <span class="text-text-muted">{{ formatTime(data.timestamp) }}</span>
                </div>
              </template>
            </Column>
            <Column field="level" header="Level" sortable style="width: 7rem">
              <template #body="{ data }">
                <Tag :value="data.level" :severity="tagSeverity(data.level)" class="uppercase text-xs font-semibold" />
              </template>
            </Column>
            <Column field="category" header="Category" sortable style="width: 9rem">
              <template #body="{ data }">
                <Tag :value="data.category" severity="secondary" class="text-xs" />
              </template>
            </Column>
            <Column field="message" header="Message" sortable>
              <template #body="{ data }">
                <div class="text-sm text-text break-words leading-snug">{{ data.message }}</div>
              </template>
            </Column>
            <Column field="duration" header="Duration" sortable style="width: 6rem" class="text-right">
              <template #body="{ data }">
                <span class="text-sm">{{ data.duration != null ? data.duration + ' ms' : '—' }}</span>
              </template>
            </Column>
            <Column field="correlationId" header="Correlation" style="width: 12rem">
              <template #body="{ data }">
                <div class="flex items-center gap-2 text-xs break-all">
                  <span class="text-text-muted">{{ data.correlationId || '—' }}</span>
                  <Button
                    v-if="data.correlationId"
                    icon="pi pi-copy"
                    severity="secondary"
                    text
                    rounded
                    size="small"
                    @click="copyText(data.correlationId)"
                  />
                </div>
              </template>
            </Column>
            <Column header="Metadata" style="width: 10rem">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <span class="text-xs text-text-muted truncate max-w-[7rem]">{{ summarizeMetadata(data.metadata) }}</span>
                  <Button icon="pi pi-eye" text rounded size="small" @click="openMetadata(data)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Metadata Dialog -->
    <Dialog v-model:visible="showMetadata" header="Log Metadata" modal class="w-[640px]">
      <div v-if="activeMetadata" class="bg-background-card border border-border rounded-lg p-3">
        <pre class="text-xs whitespace-pre-wrap break-words">{{ activeMetadata }}</pre>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Copy JSON" icon="pi pi-copy" severity="secondary" @click="copyText(activeMetadata || '')" />
          <Button label="Close" icon="pi pi-times" @click="showMetadata = false" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import dayjs from 'dayjs'
import Button from 'primevue/button'
import Calendar from 'primevue/calendar'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import type { LogCategory, LogEntry, LogLevel, LogMetadata } from '@/core/types/models/LogEntry'
import { useNotificationLog } from '@/shared/composables/useNotificationLog'

const levelOptions: { label: string; value: LogLevel | null }[] = [
  { label: 'All levels', value: null },
  { label: 'Error', value: 'error' },
  { label: 'Warn', value: 'warn' },
  { label: 'Info', value: 'info' },
  { label: 'Debug', value: 'debug' },
]

const categoryOptions: { label: string; value: LogCategory | null }[] = [
  { label: 'All categories', value: null },
  { label: 'API', value: 'api' },
  { label: 'App', value: 'app' },
  { label: 'User Action', value: 'user-action' },
  { label: 'Toast', value: 'toast' },
]

const levelFilter = ref<LogLevel | null>(null)
const categoryFilter = ref<LogCategory | null>(null)
const dateRange = ref<(Date | null)[] | null>(null)
const searchTerm = ref('')

const { logs, isLoading, fetchLogs, downloadLogs, clearAllLogs } = useNotificationLog()

const tableRows = computed<LogEntry[]>(() => logs.value as LogEntry[])

const showMetadata = ref(false)
const activeMetadata = ref<string | null>(null)

function tagSeverity(level: LogLevel): 'success' | 'info' | 'warning' | 'danger' | undefined {
  if (level === 'error') return 'danger'
  if (level === 'warn') return 'warning'
  if (level === 'info') return 'info'
  if (level === 'debug') return 'secondary' as any
  return 'info'
}

function formatDate(iso: string): string {
  return dayjs(iso).format('YYYY-MM-DD')
}

function formatTime(iso: string): string {
  return dayjs(iso).format('HH:mm:ss')
}

function summarizeMetadata(metadata?: LogMetadata): string {
  if (!metadata) return '—'
  try {
    const str = JSON.stringify(metadata)
    return str.length > 60 ? `${str.slice(0, 60)}...` : str
  } catch (e) {
    return 'Metadata'
  }
}

function openMetadata(entry: LogEntry): void {
  if (!entry.metadata) {
    activeMetadata.value = null
    showMetadata.value = false
    return
  }
  activeMetadata.value = JSON.stringify(entry.metadata, null, 2)
  showMetadata.value = true
}

async function copyText(text: string): Promise<void> {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
  } catch (e) {
    console.error('Copy failed', e)
  }
}

function buildFilters() {
  const [from, to] = dateRange.value ?? []
  const fromDate = from ?? undefined
  const toDate = to ?? undefined

  return {
    levels: levelFilter.value ? [levelFilter.value] : undefined,
    categories: categoryFilter.value ? [categoryFilter.value] : undefined,
    fromDate,
    toDate,
    searchTerm: searchTerm.value.trim() || undefined,
  }
}

async function applyFilters(): Promise<void> {
  await fetchLogs(buildFilters())
}

async function resetFilters(): Promise<void> {
  levelFilter.value = null
  categoryFilter.value = null
  dateRange.value = null
  searchTerm.value = ''
  await fetchLogs()
}

async function onRefresh(): Promise<void> {
  await applyFilters()
}

async function onExport(): Promise<void> {
  await downloadLogs(buildFilters())
}

async function onClear(): Promise<void> {
  await clearAllLogs()
  await applyFilters()
}

onMounted(async () => {
  await fetchLogs()
})

definePage({
  meta: {
    title: 'Application Logs',
    requiresProject: false,
  },
})
</script>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
  @apply bg-background-elevated text-xs font-semibold uppercase tracking-wide text-text-light;
}
:deep(.p-datatable-tbody > tr > td) {
  @apply align-top;
}
</style>
